'use client';

/**
 * AddServiceModal — productive version of the approved prototype at
 *   app/proto/services-modal/Prototype.tsx
 *
 * Behavior 1:1 with the proto (header 44px, date popover, time selects,
 * dual timezone display Chile + user country, country typeahead, pax
 * stepper, success pane). The host (ServicesCrossSell) is responsible
 * for the fixed overlay/backdrop/blur — this component renders the card
 * inline so it can be portaled or embedded.
 *
 * Wired into the unified cart via ServicesCrossSell which maps the
 * AddServicePayload returned by onConfirm into a single-segment
 * ServiceCartItem.
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import type React from 'react';
import Image from 'next/image';
import AddressAutocomplete, {
  type ResolvedAddress,
} from './AddressAutocomplete';
import { calculateTransportSegmentUSD } from '@/lib/transport-pricing';
import type { BookingSegment } from '@/lib/unified-cart';
import styles from './AddServiceModal.module.css';

/* ─────────────── I18N ─────────────── */

type Locale = 'es' | 'en' | 'pt';

interface ModalCopy {
  pickup: { label: string; placeholder: string };
  addressWarn: string;
  addressFallback: string;
  confirmBlocked: string;
  confirmReady: string;
  /** "Tarifa fija hasta {n} pax" — shown when pax ≤ baseOccupancy. */
  flatRateUpTo: (n: number) => string;
  /** "Base US$X + US$Y × N extra" — shown when extra passengers are charged. */
  flatRatePlusExtra: (base: string, extra: string, n: number) => string;
  /** Toggle labels — one-way vs round-trip. */
  tripToggleOneWay: string;
  tripToggleRoundTrip: string;
  /** Section title for the return-leg block. */
  returnSectionTitle: string;
  /** Label for the return drop-off address field. */
  returnAddressLabel: string;
  /** Hint pill shown at the bottom of the scrollable body. */
  scrollHint: string;
  /** Admin-only "additional notes" textarea label + placeholder. */
  notesLabel: string;
  notesPlaceholder: string;
}

const COPY: Record<Locale, ModalCopy> = {
  es: {
    pickup: {
      label: 'Punto de recogida',
      placeholder: 'Hotel, aeropuerto o dirección…',
    },
    addressWarn: 'Elegí una sugerencia para validar la dirección',
    addressFallback: 'Validación de dirección no disponible',
    confirmBlocked: 'Falta pickup',
    confirmReady: 'Agregar',
    flatRateUpTo: (n) => `Tarifa fija hasta ${n} pax`,
    flatRatePlusExtra: (base, extra, n) => `Base US$${base} + US$${extra} × ${n} extra`,
    tripToggleOneWay: 'Solo ida',
    tripToggleRoundTrip: 'Ida y vuelta',
    returnSectionTitle: 'Regreso',
    returnAddressLabel: 'Dirección de retorno (drop-off)',
    scrollHint: 'Bajar para continuar',
    notesLabel: 'Notas adicionales (opcional)',
    notesPlaceholder: 'Vuelo, equipaje, instrucciones para el chofer…',
  },
  en: {
    pickup: {
      label: 'Pickup point',
      placeholder: 'Hotel, airport or address…',
    },
    addressWarn: 'Pick a suggestion to validate the address',
    addressFallback: 'Address validation unavailable',
    confirmBlocked: 'Pickup required',
    confirmReady: 'Add',
    flatRateUpTo: (n) => `Flat rate up to ${n} pax`,
    flatRatePlusExtra: (base, extra, n) => `Base US$${base} + US$${extra} × ${n} extra`,
    tripToggleOneWay: 'One-way',
    tripToggleRoundTrip: 'Round-trip',
    returnSectionTitle: 'Return',
    returnAddressLabel: 'Return address (drop-off)',
    scrollHint: 'Scroll to continue',
    notesLabel: 'Additional notes (optional)',
    notesPlaceholder: 'Flight, luggage, instructions for the driver…',
  },
  pt: {
    pickup: {
      label: 'Ponto de embarque',
      placeholder: 'Hotel, aeroporto ou endereço…',
    },
    addressWarn: 'Escolha uma sugestão para validar o endereço',
    addressFallback: 'Validação de endereço indisponível',
    confirmBlocked: 'Falta embarque',
    confirmReady: 'Adicionar',
    flatRateUpTo: (n) => `Tarifa fixa até ${n} pax`,
    flatRatePlusExtra: (base, extra, n) => `Base US$${base} + US$${extra} × ${n} extra`,
    tripToggleOneWay: 'Só ida',
    tripToggleRoundTrip: 'Ida e volta',
    returnSectionTitle: 'Retorno',
    returnAddressLabel: 'Endereço de retorno (drop-off)',
    scrollHint: 'Role para continuar',
    notesLabel: 'Notas adicionais (opcional)',
    notesPlaceholder: 'Voo, bagagem, instruções para o motorista…',
  },
};

/**
 * Map an internal Country.code to an ISO-3166-1 alpha-2 country code
 * for Google `componentRestrictions`. The modal uses synthetic codes
 * (US-E / US-C / US-W) to model US timezones — collapse those to 'US'.
 */
function toIso2(internalCode: string): string {
  if (internalCode.startsWith('US-')) return 'US';
  return internalCode;
}

/* ─────────────── DATA ─────────────── */

export interface ServiceData {
  id: string;
  name: string;
  priceUSD: number;
  tagline: string;
  img: string;
  // Transport flat-rate config — needed to mirror backend pricing exactly.
  // Without these the modal would show price × pax (per-person) while the
  // backend persists a flat fare, breaking Stripe's amount-match guard.
  baseOccupancy?: number;
  extraGuestPriceUSD?: number;
  dailyPricing?: Record<string, number>;
}

const CHILE_OFFSET = -4;

interface Country {
  code: string;
  flag: string;
  name: string;
  utcOffset: number;
  label: string;
}

const COUNTRIES: Country[] = [
  { code: 'CL', flag: '🇨🇱', name: 'Chile', utcOffset: -4, label: 'CLT' },
  { code: 'AR', flag: '🇦🇷', name: 'Argentina', utcOffset: -3, label: 'ART' },
  { code: 'BR', flag: '🇧🇷', name: 'Brasil', utcOffset: -3, label: 'BRT' },
  { code: 'US-E', flag: '🇺🇸', name: 'Estados Unidos (Este)', utcOffset: -5, label: 'EST' },
  { code: 'US-C', flag: '🇺🇸', name: 'Estados Unidos (Centro)', utcOffset: -6, label: 'CST' },
  { code: 'US-W', flag: '🇺🇸', name: 'Estados Unidos (Oeste)', utcOffset: -8, label: 'PST' },
  { code: 'CA', flag: '🇨🇦', name: 'Canadá (Este)', utcOffset: -5, label: 'EST' },
  { code: 'MX', flag: '🇲🇽', name: 'México', utcOffset: -6, label: 'CST' },
  { code: 'PE', flag: '🇵🇪', name: 'Perú', utcOffset: -5, label: 'PET' },
  { code: 'CO', flag: '🇨🇴', name: 'Colombia', utcOffset: -5, label: 'COT' },
  { code: 'UY', flag: '🇺🇾', name: 'Uruguay', utcOffset: -3, label: 'UYT' },
  { code: 'ES', flag: '🇪🇸', name: 'España', utcOffset: 1, label: 'CET' },
  { code: 'GB', flag: '🇬🇧', name: 'Reino Unido', utcOffset: 0, label: 'GMT' },
  { code: 'FR', flag: '🇫🇷', name: 'Francia', utcOffset: 1, label: 'CET' },
  { code: 'DE', flag: '🇩🇪', name: 'Alemania', utcOffset: 1, label: 'CET' },
  { code: 'IT', flag: '🇮🇹', name: 'Italia', utcOffset: 1, label: 'CET' },
  { code: 'PT', flag: '🇵🇹', name: 'Portugal', utcOffset: 0, label: 'WET' },
  { code: 'CH', flag: '🇨🇭', name: 'Suiza', utcOffset: 1, label: 'CET' },
  { code: 'NL', flag: '🇳🇱', name: 'Países Bajos', utcOffset: 1, label: 'CET' },
  { code: 'AU', flag: '🇦🇺', name: 'Australia (Sídney)', utcOffset: 11, label: 'AEDT' },
  { code: 'JP', flag: '🇯🇵', name: 'Japón', utcOffset: 9, label: 'JST' },
  { code: 'CN', flag: '🇨🇳', name: 'China', utcOffset: 8, label: 'CST' },
];

const DEFAULT_COUNTRY: Country = COUNTRIES[3]; // US East

/* ─────────────── HELPERS ─────────────── */

const pad = (n: number) => n.toString().padStart(2, '0');

const NUMBER_FORMAT_ES_CL = new Intl.NumberFormat('es-CL', {
  maximumFractionDigits: 0,
});

const formatNumber = (n: number) => NUMBER_FORMAT_ES_CL.format(Math.round(n));

/** YYYY-MM-DD for "today" in client locale — used to disable past dates in the calendar. */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function convertFromChile(hour: number, minute: number, toOffset: number) {
  const totalMin = hour * 60 + minute + (toOffset - CHILE_OFFSET) * 60;
  const dayMin = 24 * 60;
  let normalized = totalMin % dayMin;
  if (normalized < 0) normalized += dayMin;
  const dayDelta = Math.floor(totalMin / dayMin);
  return {
    hour: Math.floor(normalized / 60),
    minute: normalized % 60,
    dayDelta,
  };
}

function formatTime24(hour: number, minute: number) {
  return `${pad(hour)}:${pad(minute)}`;
}

function formatTime12(hour: number, minute: number) {
  const period = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${pad(minute)} ${period}`;
}

/**
 * Time picker options — full 24h, every 15 minutes (00:00 to 23:45).
 * Single dropdown ⇒ one click to see every slot; client picks any moment.
 */
const TIME_OPTIONS: string[] = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  return `${pad(h)}:${pad(m)}`;
});

function parseTime(time: string): { hour: number; minute: number } {
  const [hStr, mStr] = time.split(':');
  return { hour: parseInt(hStr, 10), minute: parseInt(mStr, 10) };
}

function offsetLabel(offset: number) {
  const sign = offset >= 0 ? '+' : '−';
  return `UTC${sign}${Math.abs(offset)}`;
}

const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const WEEKDAYS_ES = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

function formatDateLong(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} de ${MONTHS_ES[m - 1]} ${y}`;
}

function buildCalendarGrid(year: number, month: number) {
  // month 0-based
  const first = new Date(year, month, 1);
  // 0 = Sunday → we want Monday=0
  const startWeekday = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: Array<{ day: number; iso: string } | null> = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, iso: `${year}-${pad(month + 1)}-${pad(d)}` });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/** Earliest sensible default — first of next month, in the user's locale. */
function defaultIsoDate(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1; // next month
  const year = m > 11 ? y + 1 : y;
  const month = m > 11 ? 0 : m;
  return `${year}-${pad(month + 1)}-15`;
}

function isoToMonthYear(iso: string): { year: number; month: number } {
  const [y, m] = iso.split('-').map(Number);
  return { year: y, month: m - 1 };
}

/* ─────────────── DATE FIELD ─────────────── */

interface DateFieldProps {
  value: string;
  onChange: (iso: string) => void;
  open: boolean;
  setOpen: (v: boolean | ((p: boolean) => boolean)) => void;
  month: { year: number; month: number };
  setMonth: React.Dispatch<React.SetStateAction<{ year: number; month: number }>>;
  fieldRef: React.RefObject<HTMLDivElement | null>;
  idPrefix: string;
  today: string;
  allowedSet: Set<string> | null;
  minDate?: string;
}

function DateField({
  value,
  onChange,
  open,
  setOpen,
  month,
  setMonth,
  fieldRef,
  idPrefix,
  today,
  allowedSet,
  minDate,
}: DateFieldProps) {
  const cells = buildCalendarGrid(month.year, month.month);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  // When the calendar opens, scroll it fully into view within the
  // modal's scrollable body — saves the user a manual scroll on phones
  // where the popover would otherwise render below the fold.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      popoverRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [open]);
  const navigate = (delta: number) => {
    setMonth(({ year, month: m }) => {
      let newMonth = m + delta;
      let newYear = year;
      if (newMonth < 0) {
        newMonth = 11;
        newYear -= 1;
      } else if (newMonth > 11) {
        newMonth = 0;
        newYear += 1;
      }
      return { year: newYear, month: newMonth };
    });
  };
  return (
    <div className={styles.popoverWrap} ref={fieldRef}>
      <button
        id={`add-service-date-btn-${idPrefix}`}
        className={styles.dateBtn}
        onClick={() => setOpen((v) => !v)}
        type="button"
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6">
          <rect x="3" y="4.5" width="18" height="17" rx="2" />
          <path d="M3 9h18M8 3v4M16 3v4" strokeLinecap="round" />
        </svg>
        <span>{formatDateLong(value)}</span>
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" className={open ? styles.chevronUp : ''}>
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div ref={popoverRef} className={styles.calendarPop} role="dialog" aria-label="Pick a date">
          <div className={styles.calendarHead}>
            <button className={styles.calendarNav} type="button" onClick={() => navigate(-1)} aria-label="Previous month">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span className={styles.calendarMonthLabel}>
              {MONTHS_ES[month.month]} {month.year}
            </span>
            <button className={styles.calendarNav} type="button" onClick={() => navigate(1)} aria-label="Next month">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <div className={styles.calendarWeekdays}>
            {WEEKDAYS_ES.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className={styles.calendarGrid}>
            {cells.map((cell, i) => {
              if (!cell) return <span key={i} className={styles.calendarEmpty} />;
              const isActive = cell.iso === value;
              const isPast = cell.iso < today;
              const isDisallowed = allowedSet ? !allowedSet.has(cell.iso) : false;
              const isBeforeMin = minDate ? cell.iso < minDate : false;
              const disabled = isPast || isDisallowed || isBeforeMin;
              return (
                <button
                  key={cell.iso}
                  type="button"
                  disabled={disabled}
                  className={`${styles.calendarCell} ${isActive ? styles.calendarCellActive : ''} ${disabled ? styles.calendarCellDisabled : ''}`}
                  onClick={() => {
                    if (disabled) return;
                    onChange(cell.iso);
                    setOpen(false);
                  }}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────── COMPONENT ─────────────── */

export interface AddServicePayload {
  service: string;
  service_id: string;
  price_usd: number;
  /** Length 1 (one-way) or 2 (round-trip). Each segment carries its own date/time/price. */
  segments: BookingSegment[];
  /** Outbound time label (Chile) — for cart display. */
  time_chile_label: string;
  time_user_local: string;
  time_user_label: string;
  time_user_day_delta: number;
  user_country: string;
  user_country_code: string;
  pax: number;
  /** Pickup canonical formatted address (Google `formatted_address`), or raw text in fallback. */
  pickup_address: string;
  /** Pickup Google `place_id` — empty string in fallback. */
  pickup_place_id: string;
  // Only present when segments.length === 2 (round-trip).
  return_time_chile_label?: string;
  return_time_user_local?: string;
  return_time_user_label?: string;
  return_time_user_day_delta?: number;
  return_dropoff_address?: string;
  return_dropoff_place_id?: string;
  /** Optional free-text notes (only collected when `showNotes` is on — admin flow). */
  notes?: string;
}

/**
 * Seed values for re-opening the modal to EDIT an already-configured service
 * (used by the admin pay-link flow). Every field is optional and falls back to
 * the normal first-open defaults — so the customer flow (which never passes
 * this) is unaffected.
 */
export interface AddServiceInitialValues {
  date?: string;
  time?: string;
  pax?: number;
  countryCode?: string;
  pickup?: ResolvedAddress | null;
  isRoundTrip?: boolean;
  returnDate?: string;
  returnTime?: string;
  returnDropoff?: ResolvedAddress | null;
  notes?: string;
}

export interface AddServiceModalProps {
  service: ServiceData;
  onClose?: () => void;
  onConfirm?: (payload: AddServicePayload) => void;
  /** Optional initial date (ISO YYYY-MM-DD). Falls back to today or first allowed date. */
  initialDate?: string;
  /** If provided, only these ISO dates are selectable in the calendar (typically the lodge stay range). */
  allowedDates?: string[];
  /** UI locale for new strings (pickup/dropoff/validation). Defaults to 'es'. */
  locale?: Locale;
  /**
   * When false, the round-trip drop-off address is OPTIONAL. Defaults to true
   * (the customer flow requires it). The admin pay-link flow passes false.
   */
  dropoffRequired?: boolean;
  /** When true, render an optional "Additional notes" textarea. Default false. */
  showNotes?: boolean;
  /** Seed every field when re-opening to edit (admin). Customer flow omits this. */
  initialValues?: AddServiceInitialValues;
  /** Override the confirm-button label when valid (admin uses "Save"). */
  confirmLabel?: string;
}

export default function AddServiceModal({
  service,
  onClose,
  onConfirm,
  initialDate,
  allowedDates,
  locale = 'es',
  dropoffRequired = true,
  showNotes = false,
  initialValues,
  confirmLabel,
}: AddServiceModalProps) {
  const copy = COPY[locale] ?? COPY.es;
  const allowedSet = useMemo(
    () => (allowedDates && allowedDates.length > 0 ? new Set(allowedDates) : null),
    [allowedDates],
  );
  const startDate = useMemo(() => {
    if (initialValues?.date) return initialValues.date;
    if (initialDate) return initialDate;
    if (allowedDates && allowedDates.length > 0) return allowedDates[0];
    return defaultIsoDate();
  }, [initialValues?.date, initialDate, allowedDates]);
  const [date, setDate] = useState<string>(startDate);
  const [time, setTime] = useState<string>(initialValues?.time ?? '10:30');
  const { hour, minute } = useMemo(() => parseTime(time), [time]);
  const [pax, setPax] = useState<number>(initialValues?.pax ?? 2);
  const [countryCode, setCountryCode] = useState<string>(
    initialValues?.countryCode ?? DEFAULT_COUNTRY.code,
  );
  const [pickup, setPickup] = useState<ResolvedAddress | null>(
    initialValues?.pickup ?? null,
  );
  // Admin-only free-text notes (rendered only when `showNotes`).
  const [notes, setNotes] = useState<string>(initialValues?.notes ?? '');

  // Round-trip support — second leg is optional. Defaults align with outbound
  // so the user only has to change the date/time and (optionally) the drop-off.
  const [isRoundTrip, setIsRoundTrip] = useState(initialValues?.isRoundTrip ?? false);
  const [returnDate, setReturnDate] = useState<string>(
    initialValues?.returnDate ?? startDate,
  );
  const [returnTime, setReturnTime] = useState<string>(
    initialValues?.returnTime ?? '10:30',
  );
  const [returnDropoff, setReturnDropoff] = useState<ResolvedAddress | null>(
    initialValues?.returnDropoff ?? null,
  );
  const [returnCalendarOpen, setReturnCalendarOpen] = useState(false);
  const [returnCalendarMonth, setReturnCalendarMonth] = useState(() => isoToMonthYear(startDate));
  const returnCalendarRef = useRef<HTMLDivElement | null>(null);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => isoToMonthYear(startDate));
  const [countryQuery, setCountryQuery] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);
  const [success, setSuccess] = useState(false);

  /**
   * `addressValidationActive` = the Google Maps key is configured.
   * When inactive, the AddressAutocomplete falls back to a plain input
   * and we cannot enforce a strict pick — but we still require the
   * field to be non-empty so the driver has something to read.
   */
  const addressValidationActive = Boolean(
    process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
  );

  const calendarRef = useRef<HTMLDivElement | null>(null);
  const countryRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Scroll-hint visibility — toggled when the body is scrolled to its
  // bottom (8px tolerance). Hint sits sticky inside the body and fades
  // out via the data-at-bottom attribute. Starts true so it's hidden
  // until we know the body actually overflows — onScroll() is called
  // synchronously on mount/resize to compute the real value.
  const [atBottom, setAtBottom] = useState(true);

  /* Toggle scroll-hint when user reaches the bottom of the modal body. */
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const onScroll = () => {
      const reached = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
      setAtBottom(reached);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    // Initial + on-resize check (in case content fits entirely or the
    // address-bar collapse changes the viewport height mid-session).
    onScroll();
    window.addEventListener('resize', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
    // Re-bind when the success pane swaps in/out so the listener
    // attaches to the right element after re-render.
  }, [success, isRoundTrip]);

  /* Close popovers on outside click */
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const target = e.target as Node;
      if (calendarOpen && calendarRef.current && !calendarRef.current.contains(target)) {
        setCalendarOpen(false);
      }
      if (returnCalendarOpen && returnCalendarRef.current && !returnCalendarRef.current.contains(target)) {
        setReturnCalendarOpen(false);
      }
      if (countryOpen && countryRef.current && !countryRef.current.contains(target)) {
        setCountryOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [calendarOpen, returnCalendarOpen, countryOpen]);

  const country = useMemo(
    () => COUNTRIES.find((c) => c.code === countryCode) ?? DEFAULT_COUNTRY,
    [countryCode],
  );

  const filteredCountries = useMemo(() => {
    if (!countryQuery.trim()) return COUNTRIES;
    const q = countryQuery.toLowerCase().trim();
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.label.toLowerCase().includes(q),
    );
  }, [countryQuery]);

  const userTime = useMemo(
    () => convertFromChile(hour, minute, country.utcOffset),
    [hour, minute, country.utcOffset],
  );

  // Flat-rate per-segment fare with extra-guest surcharge. Mirrors backend
  // calculateTransportSegmentPrice — keep the two in sync or Stripe's
  // amount-match guard will reject the PaymentIntent at checkout.
  const pricing = useMemo(
    () => ({
      basePriceAmount: service.priceUSD,
      baseOccupancy: service.baseOccupancy,
      extraGuestAmount: service.extraGuestPriceUSD,
      dailyPricing: service.dailyPricing,
    }),
    [service.priceUSD, service.baseOccupancy, service.extraGuestPriceUSD, service.dailyPricing],
  );
  const outboundPrice = calculateTransportSegmentUSD(pricing, { date }, pax);
  const returnPrice = isRoundTrip
    ? calculateTransportSegmentUSD(pricing, { date: returnDate }, pax)
    : 0;
  const grandTotal = outboundPrice + returnPrice;
  const today = useMemo(() => todayIso(), []);

  const handleSelectCountry = useCallback((c: Country) => {
    setCountryCode(c.code);
    setCountryQuery('');
    setCountryOpen(false);
  }, []);

  /**
   * Confirm gating: the pickup field must be a real picked suggestion
   * (with Google API) or non-empty text (in fallback mode). When round-trip
   * is selected, the return drop-off must also be valid and the return
   * date must be on or after the outbound date.
   */
  const canConfirm = useMemo(() => {
    if (!pickup) return false;
    const pickupOk = addressValidationActive
      ? !!pickup.placeId
      : pickup.formattedAddress.trim().length > 0;
    if (!pickupOk) return false;
    if (!isRoundTrip) return true;
    // Round-trip: the return leg must be on/after the outbound date. The
    // drop-off address is required only when `dropoffRequired` (the customer
    // default); the admin pay-link flow passes false to make it optional.
    if (returnDate < date) return false;
    if (!dropoffRequired) return true;
    if (!returnDropoff) return false;
    const dropoffOk = addressValidationActive
      ? !!returnDropoff.placeId
      : returnDropoff.formattedAddress.trim().length > 0;
    return dropoffOk;
  }, [pickup, returnDropoff, isRoundTrip, returnDate, date, addressValidationActive, dropoffRequired]);

  const handleConfirm = useCallback(() => {
    if (!canConfirm || !pickup) return;
    const time_chile_24h = formatTime24(hour, minute);
    const segments: BookingSegment[] = [
      {
        kind: 'outbound',
        date,
        time: time_chile_24h,
        tz: 'America/Santiago',
        priceUSD: service.priceUSD,
      },
    ];
    const time_user_local = formatTime24(userTime.hour, userTime.minute);
    const payload: AddServicePayload = {
      service: service.name,
      service_id: service.id,
      price_usd: service.priceUSD,
      segments,
      time_chile_label: `${time_chile_24h} CLT (UTC−4)`,
      time_user_local,
      time_user_label: `${time_user_local} ${country.label} (${offsetLabel(country.utcOffset)})`,
      time_user_day_delta: userTime.dayDelta,
      user_country: country.name,
      user_country_code: country.code,
      pax,
      pickup_address: pickup.formattedAddress,
      pickup_place_id: pickup.placeId,
    };
    if (isRoundTrip) {
      const { hour: rh, minute: rm } = parseTime(returnTime);
      const rUser = convertFromChile(rh, rm, country.utcOffset);
      const returnTime24 = formatTime24(rh, rm);
      segments.push({
        kind: 'return',
        date: returnDate,
        time: returnTime24,
        tz: 'America/Santiago',
        priceUSD: service.priceUSD,
      });
      const return_time_user_local = formatTime24(rUser.hour, rUser.minute);
      payload.return_time_chile_label = `${returnTime24} CLT (UTC−4)`;
      payload.return_time_user_local = return_time_user_local;
      payload.return_time_user_label = `${return_time_user_local} ${country.label} (${offsetLabel(country.utcOffset)})`;
      payload.return_time_user_day_delta = rUser.dayDelta;
      // Drop-off may be absent when `dropoffRequired` is false (admin flow).
      if (returnDropoff) {
        payload.return_dropoff_address = returnDropoff.formattedAddress;
        payload.return_dropoff_place_id = returnDropoff.placeId;
      }
    }
    if (notes.trim()) payload.notes = notes.trim();
    onConfirm?.(payload);
    setSuccess(true);
  }, [
    canConfirm,
    pickup,
    returnDropoff,
    isRoundTrip,
    returnTime,
    returnDate,
    date,
    service,
    hour,
    minute,
    userTime,
    country,
    pax,
    notes,
    onConfirm,
  ]);

  /* ─────────────── RENDER ─────────────── */

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-label="Add service">
      <div className={styles.modal}>
        {/* HEADER — service summary */}
        <header className={styles.head}>
          <div className={styles.headThumb}>
            <Image
              src={service.img}
              alt=""
              fill
              sizes="44px"
              style={{ objectFit: 'cover' }}
              unoptimized
            />
          </div>
          <div className={styles.headText}>
            <span className={styles.headEyebrow}>Add service</span>
            <h3 className={styles.headTitle}>{service.name}</h3>
            <span className={styles.headTagline}>{service.tagline}</span>
          </div>
          <button className={styles.closeBtn} aria-label="Close" type="button" onClick={onClose}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        {success ? (
          <div className={styles.body} ref={bodyRef}>
            <div className={styles.successPane}>
              <div className={styles.successIcon}>
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4 className={styles.successTitle}>Service added</h4>
              <p className={styles.successText}>
                {service.name} · {formatDateLong(date)} · {formatTime24(hour, minute)} CLT
                {isRoundTrip ? ` → ${formatDateLong(returnDate)} ${returnTime} CLT` : ''}
              </p>
              {pickup && (
                <p className={styles.successAddresses}>
                  <span>{pickup.formattedAddress}</span>
                </p>
              )}
              <button
                className={styles.successReset}
                onClick={() => {
                  setPickup(null);
                  setReturnDropoff(null);
                  setIsRoundTrip(false);
                  setSuccess(false);
                }}
                type="button"
              >
                Add another
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.body} ref={bodyRef} data-at-bottom={atBottom}>
            {/* DATE · TIME · PAX */}
            <div className={styles.controls}>
              {/* One-way / Round-trip toggle */}
              <div className={styles.tripToggle}>
                <button
                  type="button"
                  onClick={() => setIsRoundTrip(false)}
                  aria-pressed={!isRoundTrip}
                >
                  {copy.tripToggleOneWay}
                </button>
                <button
                  type="button"
                  onClick={() => setIsRoundTrip(true)}
                  aria-pressed={isRoundTrip}
                >
                  {copy.tripToggleRoundTrip}
                </button>
              </div>

              {/* Outbound date */}
              <div className={styles.row}>
                <label className={styles.rowLabel} htmlFor="add-service-date-btn-out">Date</label>
                <div className={styles.rowControl}>
                  <DateField
                    value={date}
                    onChange={setDate}
                    open={calendarOpen}
                    setOpen={setCalendarOpen}
                    month={calendarMonth}
                    setMonth={setCalendarMonth}
                    fieldRef={calendarRef}
                    idPrefix="out"
                    today={today}
                    allowedSet={allowedSet}
                  />
                </div>
              </div>

              {/* Time + Pax */}
              <div className={styles.rowSplit}>
                <div className={styles.colHalf}>
                  <label className={styles.rowLabel} htmlFor="add-service-time">Hora (Chile)</label>
                  <div className={styles.timeFields}>
                    <select
                      id="add-service-time"
                      className={styles.timeSelect}
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      onWheel={(e) => e.currentTarget.blur()}
                      aria-label="Hora"
                    >
                      {TIME_OPTIONS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className={styles.colHalf}>
                  <label className={styles.rowLabel}>Pasajeros</label>
                  <div className={styles.paxControl}>
                    <button
                      className={styles.paxBtn}
                      type="button"
                      onClick={() => setPax((n) => Math.max(1, n - 1))}
                      disabled={pax <= 1}
                      aria-label="Decrease passengers"
                    >
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12h14" strokeLinecap="round" />
                      </svg>
                    </button>
                    <span className={styles.paxCount}>{pax}</span>
                    <button
                      className={styles.paxBtn}
                      type="button"
                      onClick={() => setPax((n) => Math.min(8, n + 1))}
                      disabled={pax >= 8}
                      aria-label="Increase passengers"
                    >
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* TIMES DISPLAY */}
            <div className={styles.timesPanel}>
              <div className={styles.timeRow}>
                <span className={styles.timeFlag} aria-hidden>🇨🇱</span>
                <span className={styles.timeBody}>
                  <span className={styles.timeName}>Chile · Santiago</span>
                  <span className={styles.timeMeta}>CLT · UTC−4 · 24h</span>
                </span>
                <span className={styles.timeValue}>{formatTime24(hour, minute)}</span>
              </div>
              <div className={`${styles.timeRow} ${styles.timeRowSecondary}`}>
                <span className={styles.timeFlag} aria-hidden>{country.flag}</span>
                <span className={styles.timeBody}>
                  <span className={styles.timeName}>{country.name}</span>
                  <span className={styles.timeMeta}>
                    {country.label} · {offsetLabel(country.utcOffset)}
                    {userTime.dayDelta !== 0 && (
                      <em className={styles.dayDelta}>
                        {' '}· {userTime.dayDelta > 0 ? '+' : ''}{userTime.dayDelta} día{Math.abs(userTime.dayDelta) === 1 ? '' : 's'}
                      </em>
                    )}
                  </span>
                </span>
                <span className={styles.timeValueSecondary}>
                  {formatTime24(userTime.hour, userTime.minute)}
                  <span className={styles.timeValueAmPm}>{formatTime12(userTime.hour, userTime.minute).split(' ')[1]}</span>
                </span>
              </div>
            </div>

            {/* COUNTRY SELECTOR (typeahead) */}
            <div className={styles.row}>
              <label className={styles.rowLabel} htmlFor="add-service-country-input">Tu país</label>
              <div className={styles.rowControl}>
                <div className={styles.popoverWrap} ref={countryRef}>
                  <div className={styles.countryInputWrap}>
                    <span className={styles.countryInputFlag}>{country.flag}</span>
                    <input
                      id="add-service-country-input"
                      type="text"
                      className={styles.countryInput}
                      placeholder={country.name}
                      value={countryQuery}
                      onChange={(e) => {
                        setCountryQuery(e.target.value);
                        setCountryOpen(true);
                      }}
                      onFocus={() => setCountryOpen(true)}
                      autoComplete="off"
                      aria-expanded={countryOpen}
                    />
                    <button
                      type="button"
                      className={styles.countryChevron}
                      onClick={() => setCountryOpen((v) => !v)}
                      aria-label="Toggle country list"
                    >
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" className={countryOpen ? styles.chevronUp : ''}>
                        <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>

                  {countryOpen && (
                    <div className={styles.countryPop} role="listbox">
                      {filteredCountries.length === 0 ? (
                        <div className={styles.countryEmpty}>Sin resultados</div>
                      ) : (
                        filteredCountries.map((c) => {
                          const active = c.code === countryCode;
                          return (
                            <button
                              key={c.code}
                              type="button"
                              role="option"
                              aria-selected={active}
                              className={`${styles.countryOption} ${active ? styles.countryOptionActive : ''}`}
                              onClick={() => handleSelectCountry(c)}
                            >
                              <span className={styles.countryOptionFlag}>{c.flag}</span>
                              <span className={styles.countryOptionName}>{c.name}</span>
                              <span className={styles.countryOptionOffset}>{offsetLabel(c.utcOffset)}</span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ADDRESS — pickup only (driver always takes guest to the booked lodge) */}
            <div className={styles.addressBlock}>
              <AddressAutocomplete
                label={copy.pickup.label}
                placeholder={copy.pickup.placeholder}
                /* Bias to Chile — the destination is always the lodge in CL. */
                countryCode="CL"
                value={pickup}
                onChange={setPickup}
                warnText={copy.addressWarn}
                fallbackText={copy.addressFallback}
              />
            </div>

            {/* RETURN LEG — only when round-trip is selected. */}
            {isRoundTrip && (
              <div className={styles.controls}>
                <h4 className={styles.returnSectionTitle}>{copy.returnSectionTitle}</h4>
                <div className={styles.row}>
                  <label className={styles.rowLabel} htmlFor="add-service-date-btn-ret">Date</label>
                  <div className={styles.rowControl}>
                    <DateField
                      value={returnDate}
                      onChange={setReturnDate}
                      open={returnCalendarOpen}
                      setOpen={setReturnCalendarOpen}
                      month={returnCalendarMonth}
                      setMonth={setReturnCalendarMonth}
                      fieldRef={returnCalendarRef}
                      idPrefix="ret"
                      today={today}
                      allowedSet={allowedSet}
                      minDate={date}
                    />
                  </div>
                </div>
                <div className={styles.row}>
                  <label className={styles.rowLabel} htmlFor="add-service-time-ret">Hora (Chile)</label>
                  <div className={styles.rowControl}>
                    <div className={styles.timeFields}>
                      <select
                        id="add-service-time-ret"
                        className={styles.timeSelect}
                        value={returnTime}
                        onChange={(e) => setReturnTime(e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        aria-label="Hora regreso"
                      >
                        {TIME_OPTIONS.map((t) => (<option key={t} value={t}>{t}</option>))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className={styles.addressBlock}>
                  <AddressAutocomplete
                    label={copy.returnAddressLabel}
                    placeholder={copy.returnAddressLabel}
                    countryCode="CL"
                    value={returnDropoff}
                    onChange={setReturnDropoff}
                    warnText={copy.addressWarn}
                    fallbackText={copy.addressFallback}
                  />
                </div>
              </div>
            )}

            {/* OPTIONAL NOTES — admin flow only (showNotes). Customers never
                see this; the value rides along in the payload as `notes`. */}
            {showNotes && (
              <div className={styles.notesField}>
                <label className={styles.rowLabel} htmlFor="add-service-notes">
                  {copy.notesLabel}
                </label>
                <textarea
                  id="add-service-notes"
                  className={styles.notesInput}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={copy.notesPlaceholder}
                  rows={2}
                />
              </div>
            )}

            {/* Scroll-hint chevron — corner icon docked to bottom-right.
                Fades when the user reaches the end (data-at-bottom).
                Gradient fade on .body::after provides the second layer. */}
            <div
              className={styles.scrollHint}
              data-at-bottom={atBottom}
              aria-hidden="true"
              title={copy.scrollHint}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4">
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            </div>

            {/* FOOTER */}
            <footer className={styles.foot}>
              <div className={styles.totalBlock}>
                <span className={styles.totalLabel}>Total · {pax} pax</span>
                <span className={styles.totalValue}>US$ {formatNumber(grandTotal)}</span>
                {/* Breakdown — when round-trip, show "Ida + Vuelta"; otherwise
                    fall back to the legacy flat-rate / extra-guest explainer. */}
                {isRoundTrip ? (
                  <span className={styles.totalBreakdown}>
                    Ida US$ {formatNumber(outboundPrice)} + Vuelta US$ {formatNumber(returnPrice)}
                  </span>
                ) : (
                  service.baseOccupancy != null && (() => {
                    const baseOcc = service.baseOccupancy;
                    const extraRate = service.extraGuestPriceUSD ?? 0;
                    const extras = Math.max(0, pax - baseOcc);
                    const showExtra = extras > 0 && extraRate > 0;
                    return (
                      <span className={styles.totalBreakdown}>
                        {showExtra
                          ? copy.flatRatePlusExtra(formatNumber(service.priceUSD), formatNumber(extraRate), extras)
                          : copy.flatRateUpTo(baseOcc)}
                      </span>
                    );
                  })()
                )}
              </div>
              <button
                className={styles.confirmBtn}
                onClick={handleConfirm}
                type="button"
                disabled={!canConfirm}
                aria-disabled={!canConfirm}
                title={!canConfirm ? copy.confirmBlocked : undefined}
              >
                {canConfirm ? (confirmLabel ?? copy.confirmReady) : copy.confirmBlocked}
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
